import { useEffect, useRef, useCallback, useMemo } from "react";

interface UseDocumentProcessingPollingOptions {
  /** Documents to check for processing status */
  documents: Array<{ status: string }> | undefined;
  /** Callback to refetch data when polling */
  onRefetch: () => void;
  /** Polling interval in milliseconds (default: 3000) */
  pollingInterval?: number;
  /** Whether polling is enabled (default: true) */
  enabled?: boolean;
}

// Processing statuses - documents in these states need polling
const PROCESSING_STATUSES = ["UPLOADED", "TEXT_EXTRACTED", "KEYS_EXTRACTED"];

/**
 * Hook to poll for document processing status updates.
 * Automatically polls while documents are in processing states and stops
 * when all documents reach terminal states (READY or FAILED).
 */
const useDocumentProcessingPolling = ({
  documents,
  onRefetch,
  pollingInterval = 3000,
  enabled = true,
}: UseDocumentProcessingPollingOptions) => {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onRefetchRef = useRef(onRefetch);

  // Keep onRefetch ref up to date
  useEffect(() => {
    onRefetchRef.current = onRefetch;
  }, [onRefetch]);

  // Check if any documents are still processing (memoized for reactivity)
  const hasProcessingDocuments = useMemo(() => {
    if (!documents || documents.length === 0) return false;
    return documents.some((doc) => PROCESSING_STATUSES.includes(doc.status));
  }, [documents]);

  // Get counts for UI display
  const processingCounts = useMemo(() => {
    if (!documents || documents.length === 0) {
      return { processing: 0, ready: 0, failed: 0, total: 0 };
    }

    const processing = documents.filter((doc) =>
      PROCESSING_STATUSES.includes(doc.status)
    ).length;
    const ready = documents.filter((doc) => doc.status === "READY").length;
    const failed = documents.filter((doc) => doc.status === "FAILED").length;

    return { processing, ready, failed, total: documents.length };
  }, [documents]);

  // Stop polling helper
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Effect to manage polling based on document status
  useEffect(() => {
    if (!enabled || !hasProcessingDocuments) {
      stopPolling();
      return;
    }

    // Start polling if we have processing documents and not already polling
    if (!pollingIntervalRef.current) {
      pollingIntervalRef.current = setInterval(() => {
        onRefetchRef.current();
      }, pollingInterval);
    }

    return () => {
      stopPolling();
    };
  }, [enabled, hasProcessingDocuments, pollingInterval, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  return {
    isPolling: hasProcessingDocuments && enabled,
    hasProcessingDocuments,
    processingCounts,
    stopPolling,
  };
};

export default useDocumentProcessingPolling;
